// Personajes que pueden ser el mismo con dos nombres (#58).
//
// El guion a veces nombra a un personaje de dos formas (IRENE / IRENE SOL, LUME / DOCTORA
// LUME) y Pelip los trataba como dos actores: dos fichas, dos filas en el DOOD y dos líneas
// de presupuesto. Movie Magic hace lo mismo al importar (une mayúsculas y extensiones, pero
// IRENE e IRENE SOL quedan como dos elementos) y da una herramienta MERGE para unirlos a
// mano. Aquí no se une nada solo: se proponen los pares en los que un nombre está dentro
// del otro y decide el usuario.

export const CONTENT_TYPE_PERSONAJES_DISTINTOS = 'personajes_distintos';

/** Nombre comparable: sin extensiones de diálogo, en mayúsculas y con los espacios limpios. */
export function clavePersonaje(nombre: unknown): string {
  return String(nombre ?? '')
    .replace(/\((?:V\.?\s*O\.?|O\.?\s*S\.?|O\.?\s*C\.?|CONT'?D|CONT\.?|OFF|EN OFF|VOZ|CONTINUED)\)/gi, ' ')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, ' ');
}

const palabras = (clave: string) => clave.split(' ').filter(Boolean);

/** ¿Las palabras de `corto` aparecen seguidas dentro de `largo`? (IRENE en IRENE SOL, LUME en DOCTORA LUME) */
function contenido(corto: string, largo: string): boolean {
  const a = palabras(corto), b = palabras(largo);
  if (a.length === 0 || a.length >= b.length) return false;
  // Un nombre que es solo un número o una letra no identifica a nadie (CHICO 1 / 1).
  if (a.every((p) => /^\d+$|^.$/.test(p))) return false;
  for (let i = 0; i + a.length <= b.length; i++) {
    if (a.every((p, j) => p === b[i + j])) return true;
  }
  return false;
}

export interface PersonajeConEscenas {
  nombre: string;
  escenas: number;
}

export interface ParParecido {
  /** Clave del par, independiente del orden (para recordar «son distintos»). */
  clave: string;
  corto: PersonajeConEscenas;
  largo: PersonajeConEscenas;
}

export function claveDePar(a: string, b: string): string {
  return [clavePersonaje(a), clavePersonaje(b)].sort().join(' | ');
}

/** Pares en los que un nombre está dentro del otro, quitando los que el usuario ya dijo que son distintos. */
export function paresParecidos(personajes: PersonajeConEscenas[], distintos: Iterable<string> = []): ParParecido[] {
  const yaDecididos = new Set(distintos);
  const porClave = new Map<string, PersonajeConEscenas>();
  for (const p of personajes) {
    const k = clavePersonaje(p.nombre);
    if (!k) continue;
    const previo = porClave.get(k);
    porClave.set(k, { nombre: previo?.nombre ?? k, escenas: (previo?.escenas ?? 0) + p.escenas });
  }
  const lista = [...porClave.entries()];
  const pares: ParParecido[] = [];
  for (const [ka, a] of lista) {
    for (const [kb, b] of lista) {
      if (ka === kb || !contenido(ka, kb)) continue;
      const clave = claveDePar(ka, kb);
      if (yaDecididos.has(clave)) continue;
      pares.push({ clave, corto: a, largo: b });
    }
  }
  return pares.sort((x, y) => (y.corto.escenas + y.largo.escenas) - (x.corto.escenas + x.largo.escenas) || x.clave.localeCompare(y.clave));
}

type EntradaCast = { name?: unknown; dialogue_lines?: unknown } & Record<string, unknown>;

/** Reparto de una escena tras unir `de` en `a`: renombra y, si ya estaba `a`, deja una sola entrada sumando sus réplicas. */
export function unirEnReparto<T extends EntradaCast>(cast: T[], de: string, a: string): { cast: T[]; cambiado: boolean } {
  const kDe = clavePersonaje(de), kA = clavePersonaje(a);
  if (!Array.isArray(cast) || !cast.some((c) => clavePersonaje(c?.name) === kDe)) return { cast, cambiado: false };
  const salida: T[] = [];
  let destino = -1;
  for (const c of cast) {
    const k = clavePersonaje(c?.name);
    if (k !== kDe && k !== kA) { salida.push(c); continue; }
    if (destino < 0) {
      salida.push({ ...c, name: k === kDe ? a : c.name } as T);
      destino = salida.length - 1;
    } else {
      const d = salida[destino];
      salida[destino] = { ...d, name: a, dialogue_lines: Number(d.dialogue_lines ?? 0) + Number(c.dialogue_lines ?? 0) } as T;
    }
  }
  return { cast: salida, cambiado: true };
}

/** Lista de nombres sueltos de una escena (cast_speaking) tras la unión. */
export function unirEnNombres(nombres: unknown[], de: string, a: string): { nombres: string[]; cambiado: boolean } {
  const kDe = clavePersonaje(de), kA = clavePersonaje(a);
  if (!Array.isArray(nombres) || !nombres.some((n) => clavePersonaje(n) === kDe)) return { nombres: (nombres ?? []) as string[], cambiado: false };
  const salida: string[] = [];
  for (const n of nombres) {
    const k = clavePersonaje(n);
    const nuevo = k === kDe ? a : String(n);
    if (!salida.some((s) => clavePersonaje(s) === (k === kDe ? kA : k))) salida.push(nuevo);
  }
  return { nombres: salida, cambiado: true };
}

export function leerDistintos(contenido: unknown): string[] {
  try {
    const datos = typeof contenido === 'string' ? JSON.parse(contenido) : contenido;
    const pares = (datos as { pares?: unknown } | null)?.pares;
    return Array.isArray(pares) ? pares.filter((p): p is string => typeof p === 'string') : [];
  } catch {
    return [];
  }
}

export function serializarDistintos(pares: string[], ahora: string): string {
  return JSON.stringify({ pares: [...new Set(pares)].sort(), updated_at: ahora });
}
